import { Lock, ShieldAlert, ArrowLeft, Home } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function Forbidden() {
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { requiredRoles?: string[]; userRole?: string };
  };

  const requiredRoles = location.state?.requiredRoles ?? [];
  const userRole = location.state?.userRole ?? "";

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-slate-100/50 dark:from-slate-900 dark:via-background dark:to-slate-800/50 text-foreground">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full blur-3xl opacity-20 dark:opacity-30 bg-gradient-to-tr from-primary/30 to-primary/10" />
        <div className="absolute top-1/3 -right-20 h-64 w-64 rounded-full blur-3xl opacity-10 dark:opacity-20 bg-gradient-to-tr from-emerald-400/30 to-teal-400/10" />
        <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full blur-3xl opacity-10 dark:opacity-20 bg-gradient-to-tr from-indigo-400/30 to-fuchsia-400/10" />
      </div>

      <div className="absolute inset-0 opacity-[0.12] dark:opacity-[0.15]">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <defs>
            <pattern id="grid403" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid403)" className="text-muted-foreground" />
        </svg>
      </div>

      <div className="pointer-events-none absolute inset-0">
        {Array.from({ length: 22 }).map((_, i) => (
          <span
            key={i}
            className="absolute block h-[3px] w-[3px] rounded-full bg-foreground/30 dark:bg-foreground/40"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float403 ${6 + Math.random() * 6}s ease-in-out infinite`,
              opacity: 0.18 + Math.random() * 0.35,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative mx-auto flex min-h-screen max-w-5xl items-center px-6 py-10">
        <div className="w-full">
          <div className="mx-auto max-w-2xl rounded-3xl card-hover border-0 shadow-modern-md bg-card/90 backdrop-blur-xl p-8">
            {/* Header */}
            <div className="mb-6 flex items-center gap-3">
              <div className="rounded-2xl bg-primary/10 p-3 ring-1 ring-primary/15">
                <ShieldAlert className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Access Restricted</h1>
                <p className="text-sm text-muted-foreground">You don’t have permission to view this page.</p>
              </div>
            </div>

            {/* Centerpiece */}
            <div className="relative mx-auto my-10 flex max-w-lg flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute -inset-2 rounded-full bg-gradient-to-tr from-primary/25 via-primary/10 to-primary/20 blur-xl" />
                <div className="relative rounded-full border border-border/60 bg-card p-6">
                  <Lock className="h-12 w-12 text-foreground" />
                </div>
              </div>

              <div className="space-y-2">
                <h2 className="text-5xl font-black tracking-tight">
                  <span className="bg-gradient-to-r from-foreground via-foreground/80 to-foreground/60 bg-clip-text text-transparent">
                    403
                  </span>
                </h2>
                <p className="text-muted-foreground">
                  Looks like your current role doesn’t unlock this route.
                </p>
                <p className="text-xs text-muted-foreground">
                  If you think this is a mistake, contact your admin to request access.
                </p>

                {/* If ProtectedRoute passed details, show them unobtrusively */}
                {(requiredRoles.length > 0 || userRole) && (
                  <div className="mt-4 inline-flex flex-col items-center gap-1 rounded-lg border border-border/60 bg-muted/40 px-3 py-2">
                    {requiredRoles.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Required role(s): <span className="font-medium text-foreground">{requiredRoles.join(", ")}</span>
                      </p>
                    )}
                    {userRole && (
                      <p className="text-xs text-muted-foreground">
                        Your role: <span className="font-medium text-foreground">{userRole}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button
                  onClick={() => navigate(-1)}
                  variant="outline"
                  className="group border-border bg-background/70 "
                >
                  <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                  Go Back
                </Button>
                <Link to="/dashboard">
                  <Button className="group bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-black text-white shadow-modern-sm hover:shadow-modern-md">
                    <Home className="mr-2 h-4 w-4 transition-transform group-hover:-translate-y-0.5" />
                    Dashboard
                  </Button>
                </Link>
              </div>
            </div>

            {/* Footer hint */}
            <div className="mt-8 rounded-xl border border-border/60 bg-muted/40 p-4 text-left">
              <p className="text-xs text-muted-foreground">
                Tip: Ask your administrator to add your account to the required role(s) for this route.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* local keyframes (scoped name to avoid collisions) */}
      <style>{`
        @keyframes float403 {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
          100% { transform: translateY(0px); }
        }
      `}</style>
    </div>
  );
}
